import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { lightBlue, theme, blueGrey } from '../design-system/theme';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { Template, ConsentForm } from '../types/admin';
import SendConsentModal from '../components/SendConsentModal';
import { FormBuilder } from '../components/admin';
import { ViewModal, Section, SectionTitle, InfoGrid, InfoCard, InfoLabel, InfoValue, Badge, TagList, Tag } from '../components/admin/ViewModal';
import { DevicePreviewFrame } from '../components/preview/DevicePreviewFrame';
import { TemplatePreview } from '../components/preview/TemplatePreview';
import {
  PageHeader,
  PageTitle,
  FilterGroup,
  SearchInput,
  PrimaryButton,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ActionButtons,
  Card,
  CardHeader,
  CardTitle,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  CloseButton,
  Select,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
} from '../styles/adminShared';

// =============================================================================
// PAGE-SPECIFIC STYLED COMPONENTS
// =============================================================================

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: ${blueGrey[1]};
  border-radius: 8px;
  margin-bottom: 20px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? theme.primary : theme.textSecondary};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  
  &:hover {
    color: ${theme.primary};
  }
`;

const FullPageModalOverlay = styled(ModalOverlay)`
  padding: 20px;
`;

const FullPageContent = styled(ModalContent)`
  max-width: 1400px;
  background: #f8f9fa;
`;

const FullPageHeader = styled(ModalHeader)`
  background: white;
  border-radius: 16px 16px 0 0;
  
  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const FullPageBody = styled(ModalBody)`
  background: #f8f9fa;
`;

const SearchFilterRow = styled(FilterGroup)`
  margin-left: auto;
`;

const TemplateTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${lightBlue[1]};
  color: ${lightBlue[7]};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 4px;
`;

const ActionGroup = styled(ActionButtons)``;

// Custom small button variant for table actions
const SmallIconButton = styled(IconButton)`
  padding: 6px 12px;
  font-size: 12px;
`;

// =============================================================================
// COMPONENT
// =============================================================================

export const ConsentForms: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showBuilder, setShowBuilder] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewModalTab, setViewModalTab] = useState<'details' | 'preview'>('details');
  const [selectedForm, setSelectedForm] = useState<ConsentForm | null>(null);
  const [editForm, setEditForm] = useState<ConsentForm | null>(null);
  
  // Send modal state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [selectedFormForSend, setSelectedFormForSend] = useState<ConsentForm | null>(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [templatesData, formsData] = await Promise.all([
        adminApi.listTemplates(undefined, 0, 100),
        adminApi.listForms(1, 100),
      ]);
      
      const templateList = templatesData.data || templatesData.content || [];
      // Filter templates that can be used in forms - include ACTIVE, DRAFT and legacy data (null/undefined)
      // Exclude only INACTIVE and ARCHIVED templates
      setTemplates(templateList.filter((t: Template) => 
        t.status === 'ACTIVE' || 
        t.status === 'DRAFT' ||
        t.status === null || 
        t.status === undefined
      ));
      setForms(formsData.data || formsData.content || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Stats calculations
  const stats = useMemo(() => {
    const total = forms.length;
    const active = forms.filter(f => f.active !== false).length;
    const totalConsents = forms.reduce((sum, f) => sum + (f.consentCount || 0), 0);
    return { total, active, inactive: total - active, totalConsents };
  }, [forms]);

  // Filtered forms
  const filteredForms = useMemo(() => {
    return forms.filter(form => {
      const matchesSearch = !searchTerm || 
        (form.formTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (form.formId || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && form.active !== false) ||
        (statusFilter === 'inactive' && form.active === false);
      
      return matchesSearch && matchesStatus;
    });
  }, [forms, searchTerm, statusFilter]);

  // Get template names for a form
  const getTemplateNames = (templateIds: string[] | undefined): string[] => {
    if (!templateIds) return [];
    return templateIds.map(id => {
      const template = templates.find(t => t.templateId === id);
      return template?.name || template?.templateName || id.substring(0, 8);
    });
  };

  // Get combined template data for preview
  const getFormPreviewData = (form: ConsentForm) => {
    if (!form.templateIds) return { consentText: '', dataCategories: [], evidenceTypes: ['signature'], expiryDays: 365 };
    
    const linkedTemplates = form.templateIds
      .map(id => templates.find(t => t.templateId === id))
      .filter(Boolean) as Template[];
    
    // Combine consent text from all templates
    const consentText = linkedTemplates
      .map(t => t.consentText || '')
      .filter(Boolean)
      .join('<hr style="margin: 20px 0; border: none; border-top: 1px solid #e5e5e5;" />');
    
    // Combine data categories
    const dataCategories = [...new Set(linkedTemplates.flatMap(t => t.dataCategories || []))];
    
    // Use form's evidence types or fallback to templates
    const evidenceTypes = form.evidenceTypes || linkedTemplates.flatMap(t => t.evidenceTypes || ['signature']);
    
    // Use minimum expiry days from templates
    const expiryDays = Math.min(...linkedTemplates.map(t => t.expiryDays || 365));
    
    return { consentText, dataCategories, evidenceTypes, expiryDays };
  };

  // Handlers
  const handleCreateForm = async (formData: Partial<ConsentForm>) => {
    try {
      await adminApi.createForm({
        formTitle: formData.formTitle || '',
        formDescription: formData.formDescription,
        templateIds: formData.templateIds || [],
        active: true,
      });
      setSuccess('Consent form created successfully!');
      setShowBuilder(false);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create form');
    }
  };

  const handleEditForm = async (formData: Partial<ConsentForm>) => {
    if (!editForm?.formId) return;
    try {
      await adminApi.updateForm(editForm.formId, {
        formTitle: formData.formTitle,
        formDescription: formData.formDescription,
        templateIds: formData.templateIds,
        active: formData.active,
      });
      setSuccess('Consent form updated successfully!');
      setShowBuilder(false);
      setEditForm(null);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update form');
    }
  };

  const handleView = (form: ConsentForm) => {
    setSelectedForm(form);
    setShowViewModal(true);
  };

  const handleEdit = async (form: ConsentForm) => {
    try {
      // Fetch full form details (list might not include all data)
      const fullForm = await adminApi.getForm(form.formId!);
      setEditForm(fullForm);
      setShowBuilder(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load form for editing');
    }
  };

  const handleSend = (form: ConsentForm) => {
    setSelectedFormForSend(form);
    setSendModalOpen(true);
  };

  const handleToggleStatus = async (form: ConsentForm) => {
    if (!form.formId) return;
    try {
      await adminApi.updateForm(form.formId, {
        ...form,
        active: form.active === false,
      });
      setSuccess(`Form ${form.active === false ? 'activated' : 'deactivated'} successfully`);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update form status');
    }
  };

  return (
    <AdminShell>
      <PageHeader>
        <PageTitle>📋 Consent Forms</PageTitle>
        <PrimaryButton onClick={() => { setEditForm(null); setShowBuilder(true); }}>
          + Create Form
        </PrimaryButton>
      </PageHeader>

      {error && <AlertMessage $variant="error">⚠️ {error}</AlertMessage>}
      {success && <AlertMessage $variant="success">✅ {success}</AlertMessage>}

      {/* Stats Row - Inline subtle pills */}
      <StatsGrid>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.active}</StatValue>
          <StatLabel>Active</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.inactive}</StatValue>
          <StatLabel>Inactive</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalConsents}</StatValue>
          <StatLabel>Consents</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* Forms Table */}
      <Card>
        <CardHeader>
          <CardTitle>📜 All Consent Forms</CardTitle>
          <SearchFilterRow>
            <SearchInput
              type="text"
              placeholder="Search forms..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSearchTerm(e.target.value)}
            />
            <Select value={statusFilter} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setStatusFilter(e.target.value as any)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </SearchFilterRow>
        </CardHeader>

        {loading ? (
          <LoadingSpinner>
            <div>Loading forms...</div>
          </LoadingSpinner>
        ) : filteredForms.length === 0 ? (
          <EmptyStateContainer>
            <EmptyStateIcon>📋</EmptyStateIcon>
            <EmptyStateTitle>{forms.length === 0 ? 'No Consent Forms Yet' : 'No Matching Forms'}</EmptyStateTitle>
            <EmptyStateText>
              {forms.length === 0 
                ? 'Create your first consent form to start collecting consent from data owners.'
                : 'Try adjusting your search or filters.'}
            </EmptyStateText>
            {forms.length === 0 && (
              <PrimaryButton onClick={() => setShowBuilder(true)}>
                + Create First Form
              </PrimaryButton>
            )}
          </EmptyStateContainer>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Form Title</Th>
                <Th>Templates</Th>
                <Th>Created</Th>
                <Th>Status</Th>
                <Th>Consents</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredForms.map((form) => (
                <Tr key={form.formId}>
                  <Td>
                    <strong>{form.formTitle || 'Untitled Form'}</strong>
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>
                      ID: {form.formId?.substring(0, 12)}...
                    </div>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {getTemplateNames(form.templateIds).map((name, idx) => (
                        <TemplateTag key={idx}>{name}</TemplateTag>
                      ))}
                      {(!form.templateIds || form.templateIds.length === 0) && (
                        <span style={{ color: '#95a5a6', fontSize: '13px' }}>No templates</span>
                      )}
                    </div>
                  </Td>
                  <Td style={{ color: '#7f8c8d' }}>
                    {form.createdAt ? new Date(form.createdAt).toLocaleDateString() : '-'}
                  </Td>
                  <Td>
                    <StatusBadge $status={form.active !== false ? 'ACTIVE' : 'INACTIVE'}>
                      {form.active !== false ? '● Active' : '○ Inactive'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <strong>{form.consentCount || 0}</strong>
                  </Td>
                  <Td>
                    <ActionGroup>
                      <SmallIconButton $variant="view" onClick={() => handleView(form)}>
                        👁️ View
                      </SmallIconButton>
                      <SmallIconButton $variant="edit" onClick={() => handleEdit(form)}>
                        ✏️ Edit
                      </SmallIconButton>
                      <SmallIconButton $variant="success" onClick={() => handleSend(form)}>
                        📤 Send
                      </SmallIconButton>
                      <SmallIconButton 
                        $variant="edit"
                        onClick={() => handleToggleStatus(form)}
                      >
                        {form.active !== false ? '⏸️ Disable' : '▶️ Enable'}
                      </SmallIconButton>
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Form Builder Modal */}
      <FullPageModalOverlay $show={showBuilder}>
        <FullPageContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <FullPageHeader>
            <h2>
              {editForm ? '✏️ Edit Consent Form' : '📋 Create Consent Form'}
            </h2>
            <CloseButton onClick={() => { setShowBuilder(false); setEditForm(null); }}>
              &times;
            </CloseButton>
          </FullPageHeader>
          <FullPageBody>
            <FormBuilder
              templates={templates}
              initialData={editForm || undefined}
              onSave={editForm ? handleEditForm : handleCreateForm}
              onCancel={() => { setShowBuilder(false); setEditForm(null); }}
              isEdit={!!editForm}
            />
          </FullPageBody>
        </FullPageContent>
      </FullPageModalOverlay>

      {/* View Modal */}
      <ViewModal
        show={showViewModal}
        onClose={() => { setShowViewModal(false); setSelectedForm(null); setViewModalTab('details'); }}
        title={selectedForm?.formTitle || 'Consent Form Details'}
        icon="📋"
        width="1000px"
      >
        {selectedForm && (
          <>
            <TabContainer>
              <Tab $active={viewModalTab === 'details'} onClick={() => setViewModalTab('details')}>
                📋 Details
              </Tab>
              <Tab $active={viewModalTab === 'preview'} onClick={() => setViewModalTab('preview')}>
                👁️ Live Preview
              </Tab>
            </TabContainer>
            
            {viewModalTab === 'details' && (
              <>
                <Section>
                  <SectionTitle>📝 Basic Information</SectionTitle>
                  <InfoGrid>
                    <InfoCard>
                      <InfoLabel>Form ID</InfoLabel>
                      <InfoValue $mono>{selectedForm.formId || '-'}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Form Title</InfoLabel>
                      <InfoValue>{selectedForm.formTitle || 'Untitled'}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Description</InfoLabel>
                      <InfoValue>{selectedForm.formDescription || 'No description'}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Status</InfoLabel>
                      <InfoValue>
                        <Badge $variant={selectedForm.active !== false ? 'success' : 'default'}>
                          {selectedForm.active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </InfoValue>
                    </InfoCard>
                  </InfoGrid>
                </Section>

                <Section>
                  <SectionTitle>📄 Linked Templates</SectionTitle>
                  {selectedForm.templateIds && selectedForm.templateIds.length > 0 ? (
                    <TagList>
                      {getTemplateNames(selectedForm.templateIds).map((name, idx) => (
                        <Tag key={idx}>{name}</Tag>
                      ))}
                    </TagList>
                  ) : (
                    <p style={{ color: '#7f8c8d' }}>No templates linked</p>
                  )}
                </Section>

                <Section>
                  <SectionTitle>🔐 Evidence Types</SectionTitle>
                  {selectedForm.evidenceTypes && selectedForm.evidenceTypes.length > 0 ? (
                    <TagList>
                      {selectedForm.evidenceTypes.map((type, idx) => (
                        <Tag key={idx}>{type}</Tag>
                      ))}
                    </TagList>
                  ) : (
                    <TagList>
                      <Tag>SIGNATURE</Tag>
                    </TagList>
                  )}
                </Section>

                <Section>
                  <SectionTitle>📊 Statistics</SectionTitle>
                  <InfoGrid>
                    <InfoCard>
                      <InfoLabel>Total Consents</InfoLabel>
                      <InfoValue>{selectedForm.consentCount || 0}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Created</InfoLabel>
                      <InfoValue>{selectedForm.createdAt ? new Date(selectedForm.createdAt).toLocaleString() : '-'}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Updated</InfoLabel>
                      <InfoValue>{selectedForm.updatedAt ? new Date(selectedForm.updatedAt).toLocaleString() : '-'}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Created By</InfoLabel>
                      <InfoValue>{selectedForm.createdBy || '-'}</InfoValue>
                    </InfoCard>
                  </InfoGrid>
                </Section>

                {(selectedForm.successRedirectUrl || selectedForm.failureRedirectUrl) && (
                  <Section>
                    <SectionTitle>↩️ Redirect URLs</SectionTitle>
                    <InfoGrid>
                      {selectedForm.successRedirectUrl && (
                        <InfoCard>
                          <InfoLabel>Success URL</InfoLabel>
                          <InfoValue $mono>{selectedForm.successRedirectUrl}</InfoValue>
                        </InfoCard>
                      )}
                      {selectedForm.failureRedirectUrl && (
                        <InfoCard>
                          <InfoLabel>Failure URL</InfoLabel>
                          <InfoValue $mono>{selectedForm.failureRedirectUrl}</InfoValue>
                        </InfoCard>
                      )}
                    </InfoGrid>
                  </Section>
                )}
              </>
            )}
            
            {viewModalTab === 'preview' && (() => {
              const previewData = getFormPreviewData(selectedForm);
              return (
                <DevicePreviewFrame defaultDevice="mobile" maxHeight={550}>
                  <TemplatePreview
                    consentText={previewData.consentText}
                    templateName={selectedForm.formTitle}
                    description={selectedForm.formDescription}
                    dataCategories={previewData.dataCategories}
                    expiryDays={previewData.expiryDays}
                    evidenceTypes={previewData.evidenceTypes}
                  />
                </DevicePreviewFrame>
              );
            })()}
          </>
        )}
      </ViewModal>

      {/* Send Consent Modal */}
      {selectedFormForSend && (
        <SendConsentModal
          isOpen={sendModalOpen}
          onClose={() => {
            setSendModalOpen(false);
            setSelectedFormForSend(null);
          }}
          formId={selectedFormForSend.formId || ''}
          formTitle={selectedFormForSend.formTitle}
          evidenceTypes={selectedFormForSend.evidenceTypes || ['SIGNATURE']}
          successRedirectUrl={selectedFormForSend.successRedirectUrl}
          failureRedirectUrl={selectedFormForSend.failureRedirectUrl}
        />
      )}
    </AdminShell>
  );
};

export default ConsentForms;
